import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';
import {
  Compass, Sparkles, Crown, ShieldCheck, Lightbulb, HeartHandshake,
  TrendingUp, MapPin, Building2, Calculator, BookOpen, Users,
  Target, Rocket, Eye, Award, Mail, Phone, ArrowRight, CheckCircle2,
  Quote, Globe2, Trophy, Cpu, IndianRupee, Receipt, Home as HomeIcon,
  Hammer, ScrollText, GraduationCap, Linkedin, Twitter
} from 'lucide-react';

const Stat = ({ value, label, accent }) => (
  <div className="text-center">
    <p className={`font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold ${accent || 'text-[#0F172A]'}`}>{value}</p>
    <p className="text-xs md:text-sm uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
  </div>
);

const SectionTitle = ({ eyebrow, title, kicker }) => (
  <div className="max-w-3xl">
    {eyebrow && (
      <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-3">{eyebrow}</p>
    )}
    <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-5xl font-bold text-[#0F172A] leading-tight">
      {title}
    </h2>
    {kicker && <p className="text-muted-foreground text-base md:text-lg mt-4 leading-relaxed">{kicker}</p>}
  </div>
);

const AboutUsPage = () => {
  const problems = [
    {
      icon: Compass,
      title: 'No proper Vastu tooling',
      body: 'Indian buyers care deeply about Vastu Shastra — direction, entry, kitchen placement, brahmasthan. Yet not a single major portal lets you filter or evaluate a home on Vastu parameters. Buyers either skip listings blindly or pay ₹5,000+ to a separate consultant. We are fixing this.'
    },
    {
      icon: Receipt,
      title: 'Hidden brokerage & opaque pricing',
      body: 'Most platforms let agents quote 1–2% brokerage that magically appears at site visit. We removed it. ₹0 brokerage from Bharat Estate. Owners pay one transparent membership fee — buyers pay nothing.'
    },
    {
      icon: Calculator,
      title: 'Calculators scattered across 10 sites',
      body: 'EMI on one site, stamp duty on a state portal, rental yield on a spreadsheet, affordability nowhere. We consolidated 7 calculators + 2 quizzes under one roof so you decide with the full picture in 5 minutes.'
    },
    {
      icon: ShieldCheck,
      title: 'Fake listings & ghost owners',
      body: 'You call a number, it rings a broker. You ask for the owner, you get silence. Bharat Estate gates property creation behind a paid Premium membership and verified profiles — fake supply gets economically priced out.'
    },
    {
      icon: ScrollText,
      title: 'No clarity on RERA, stamp duty, or possession',
      body: 'First-time buyers do not know which state charges what stamp duty, what RERA registration looks like, or what BHK actually means in carpet vs super built-up. Our Glossary, Stamp Duty Calculator, and Market Insights answer these questions for free.'
    },
    {
      icon: HeartHandshake,
      title: 'Tier-2 & Tier-3 cities ignored',
      body: 'Most luxury platforms stop at Mumbai-Bangalore-Delhi. Real Bharat lives in Indore, Bhubaneswar, Coimbatore, Lucknow. We are building city by city — already serving 24+ Indian cities and counting.'
    },
  ];

  const values = [
    { icon: Target, title: 'Truth in listings', body: 'Verified premium-only seller pool. No ghost brokers, no inflated photos.' },
    { icon: IndianRupee, title: 'Indian-first pricing', body: 'INR pricing, ₹99 entry membership, GST-compliant invoicing — no dollar gimmicks.' },
    { icon: Globe2, title: 'Bharat, not just metros', body: 'Tier-2 and Tier-3 cities matter. Indore deserves the same UX as Bandra.' },
    { icon: Cpu, title: 'Tech that respects you', body: 'Fast. Mobile-first. Zero spam emails. Your data stays yours.' },
    { icon: Sparkles, title: 'Cultural fluency', body: 'Vastu, family priorities, joint ownership, NRI buyers — built into the product.' },
    { icon: Award, title: 'Founder-led support', body: 'Email contact@bharat.estate and you might hear back from Bhavik or Ronak directly.' },
  ];

  const milestones = [
    {
      year: '2024',
      title: 'The frustration',
      body: 'Ronak tries to buy his first home in Pune. After 6 months, 47 site visits, and ₹85,000 wasted on “premium leads”, he calls his elder brother Bhavik. By the end of that call, they realise the entire stack is broken — and that they are going to fix it.'
    },
    {
      year: 'Q3 2025',
      title: 'The first sketch',
      body: 'Bharat Estate gets named on a flight from Hyderabad to Bengaluru. The first wireframes — EMI calculator, owner-direct listings, Vastu filters — are drawn on three boarding passes.'
    },
    {
      year: 'Q4 2025',
      title: 'Bharat Estate incorporated',
      body: 'The company is incorporated. First two engineers join. Initial prototype runs on a single laptop with a SQLite database.'
    },
    {
      year: 'Jan 2026',
      title: 'Private alpha',
      body: 'First 50 listings across Mumbai and Pune. Email auth, EMI calculator, premium membership in early form.'
    },
    {
      year: 'Feb 2026',
      title: 'Public launch',
      body: '4-tier Premium plans (₹99 / ₹199 / ₹299 / ₹499). Razorpay live mode. 7 calculators + Property Match Quiz + Knowledge Quiz. Coverage hits 24+ cities.'
    },
    {
      year: 'Coming soon',
      title: 'Vastu engine + AI matchmaker',
      body: 'India-first Vastu compliance scoring on every listing, AI-powered property matching trained on Indian buyer behaviour, WhatsApp-first agent flow.'
    },
  ];

  const founderHighlights = [
    { icon: GraduationCap, label: 'Combined experience', value: '15+ years building consumer products' },
    { icon: Hammer, label: 'Built before this', value: 'Multiple ventures across India &amp; North America' },
    { icon: Trophy, label: 'What drives them', value: 'Making first-home purchases boring (in a good way)' },
    { icon: HomeIcon, label: 'Personal stake', value: 'Their own family is on the platform too' },
  ];

  const tools = [
    { icon: Calculator, name: 'EMI Calculator', href: '/emi-calculator' },
    { icon: IndianRupee, name: 'Affordability Calculator', href: '/affordability-calculator' },
    { icon: Receipt, name: 'Stamp Duty Calculator', href: '/stamp-duty-calculator' },
    { icon: TrendingUp, name: 'Rental Yield Calculator', href: '/rental-yield-calculator' },
    { icon: Sparkles, name: 'Property Match Quiz', href: '/quiz' },
    { icon: Trophy, name: 'Real Estate Knowledge Quiz', href: '/knowledge-quiz' },
    { icon: BookOpen, name: 'Glossary (30+ terms)', href: '/glossary' },
    { icon: TrendingUp, name: 'Market Insights — 8 cities', href: '/market-trends' },
  ];

  const cities = [
    'Mumbai', 'Pune', 'Bengaluru', 'Hyderabad', 'Delhi NCR', 'Gurgaon',
    'Noida', 'Chennai', 'Kolkata', 'Ahmedabad', 'Indore', 'Bhopal',
    'Lucknow', 'Jaipur', 'Chandigarh', 'Coimbatore', 'Bhubaneswar', 'Surat',
    'Vadodara', 'Nashik', 'Nagpur', 'Visakhapatnam', 'Kochi', 'Goa',
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="about-us-page">
      <Seo
        title="About Bharat Estate · Co-Founders Bhavik &amp; Ronak Rana · Our Story"
        description="The story behind Bharat Estate. Why we are building India's most honest real estate platform — Vastu-aware, broker-free, and built for Bharat. Co-founded by brothers Bhavik Rana (CEO) and Ronak Rana (COO)."
        path="/about"
      />

      {/* HERO */}
      <section className="relative pt-16 pb-24 overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 80%, #D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-luxury relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/15 text-[#D4AF37] rounded-full text-sm font-medium mb-6 border border-[#D4AF37]/30">
              <Crown className="h-4 w-4" /> Our Story
            </div>
            <h1 className="font-['Plus_Jakarta_Sans'] text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
              We are building real estate <span className="text-[#D4AF37]">for Bharat</span>, not just for boardrooms.
            </h1>
            <p className="text-white/75 text-lg md:text-xl leading-relaxed max-w-3xl">
              Bharat Estate exists because buying or renting a home in India is unnecessarily painful — opaque brokerage,
              fake listings, no Vastu tooling, calculators scattered across ten websites, and Tier-2 cities treated as
              afterthoughts. We are the antidote.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link to="/properties?listing_type=buy">
                <Button className="bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-bold px-7 py-6 text-base" data-testid="about-cta-explore">
                  Explore Properties <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/premium">
                <Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 px-7 py-6 text-base" data-testid="about-cta-premium">
                  See Premium Plans
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 mt-20 pt-10 border-t border-white/10">
            <Stat value="24+" label="Cities Served" accent="text-[#D4AF37]" />
            <Stat value="₹99" label="Premium Starts" accent="text-[#D4AF37]" />
            <Stat value="0%" label="Brokerage" accent="text-[#D4AF37]" />
            <Stat value="7" label="Free Calculators" accent="text-[#D4AF37]" />
          </div>
        </div>
      </section>

      {/* MISSION / VISION / VALUES STRIP */}
      <section className="py-20 bg-white">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div data-testid="about-mission-card">
              <Target className="h-10 w-10 text-[#D4AF37] mb-4" />
              <h3 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A] mb-3">Mission</h3>
              <p className="text-muted-foreground leading-relaxed">
                Make every home decision in India informed, affordable, and honest. Cut out brokerage. Cut out fake
                listings. Put first-class tools — EMI, affordability, stamp duty, Vastu — in every buyer&apos;s hand for
                free.
              </p>
            </div>
            <div data-testid="about-vision-card">
              <Eye className="h-10 w-10 text-[#D4AF37] mb-4" />
              <h3 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A] mb-3">Vision</h3>
              <p className="text-muted-foreground leading-relaxed">
                Be the default first stop for any Indian considering a home — to buy, rent, sell, or simply understand.
                One platform, every city, full transparency, culturally fluent.
              </p>
            </div>
            <div data-testid="about-values-card">
              <HeartHandshake className="h-10 w-10 text-[#D4AF37] mb-4" />
              <h3 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A] mb-3">Promise</h3>
              <p className="text-muted-foreground leading-relaxed">
                Zero brokerage from us. No spam. No bait listings. No surprise fees. If we ever break that promise,
                email us directly and the founders themselves will personally make it right.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-24 bg-[#F9F9F7]">
        <div className="container-luxury">
          <SectionTitle
            eyebrow="The Problem"
            title="What's broken with Indian real estate, today."
            kicker="Before writing a single line of code, we spent six months talking to 200+ buyers, sellers, and brokers across nine cities. These six themes came up in every conversation."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {problems.map((p, i) => (
              <div key={i} className="bg-white p-7 rounded-sm border border-border/50 hover:shadow-lg transition-all" data-testid={`problem-card-${i}`}>
                <div className="h-12 w-12 rounded-sm bg-[#0F172A] text-[#D4AF37] flex items-center justify-center mb-5">
                  <p.icon className="h-6 w-6" />
                </div>
                <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold text-[#0F172A] mb-3 leading-snug">
                  {p.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CO-FOUNDERS */}
      <section className="py-24 bg-white" id="founder">
        <div className="container-luxury">
          <div className="max-w-3xl mb-14">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Meet the Co-Founders</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] leading-tight mb-5">
              Two brothers, one mission — to make Indian home decisions honest.
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Bharat Estate was co-founded by brothers <strong className="text-[#0F172A]">Bhavik Rana</strong> and{' '}
              <strong className="text-[#0F172A]">Ronak Rana</strong> — born in the same household that taught them every
              middle-class Indian buys exactly one home in their lifetime, and that one decision deserves a platform
              that respects them. They incorporated the company in late 2025 and launched Bharat Estate three months later.
            </p>
          </div>

          {/* The two founder cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* CEO — Bhavik */}
            <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-sm overflow-hidden relative p-8 md:p-10" data-testid="cofounder-bhavik">
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(135deg, #D4AF37 0%, transparent 50%)' }} />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="h-24 w-24 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-2xl">
                    <span className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#0F172A]">BR</span>
                  </div>
                  <div className="flex gap-2">
                    <a href="mailto:contact@bharat.estate" className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-[#D4AF37] transition-colors">
                      <Mail className="h-4 w-4" />
                    </a>
                    <a href="#" aria-label="LinkedIn" className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-[#D4AF37] transition-colors">
                      <Linkedin className="h-4 w-4" />
                    </a>
                    <a href="#" aria-label="Twitter" className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-[#D4AF37] transition-colors">
                      <Twitter className="h-4 w-4" />
                    </a>
                  </div>
                </div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold mb-1">Co-Founder &amp; CEO</p>
                <h3 className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-white leading-tight mb-3">Bhavik Rana</h3>
                <p className="text-white/70 text-sm mb-5">Strategy · Capital · International expansion</p>
                <p className="text-white/80 leading-relaxed text-sm mb-4">
                  Bhavik leads strategy, capital, and the long-term roadmap. After years of building consumer and
                  fintech ventures between India and North America, he came home with a single question:{' '}
                  <em className="text-white">why does buying a home in India have to feel like a punishment?</em> He
                  set the company&apos;s zero-brokerage policy on day one and personally negotiated the GST-compliant
                  pricing framework you see on the Premium page.
                </p>
                <p className="text-white/80 leading-relaxed text-sm">
                  He also leads our international corridor — supporting NRI buyers across the US, Canada, UAE, and UK
                  who want a real, accountable Indian property partner.
                </p>
              </div>
            </div>

            {/* COO — Ronak */}
            <div className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] rounded-sm overflow-hidden relative p-8 md:p-10" data-testid="cofounder-ronak">
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(225deg, #D4AF37 0%, transparent 50%)' }} />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="h-24 w-24 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-2xl">
                    <span className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#0F172A]">RR</span>
                  </div>
                  <div className="flex gap-2">
                    <a href="mailto:contact@bharat.estate" className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-[#D4AF37] transition-colors" data-testid="founder-email-link">
                      <Mail className="h-4 w-4" />
                    </a>
                    <a href="#" aria-label="LinkedIn" className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-[#D4AF37] transition-colors">
                      <Linkedin className="h-4 w-4" />
                    </a>
                    <a href="#" aria-label="Twitter" className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-[#D4AF37] transition-colors">
                      <Twitter className="h-4 w-4" />
                    </a>
                  </div>
                </div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold mb-1">Co-Founder &amp; COO</p>
                <h3 className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-white leading-tight mb-3">Ronak Rana</h3>
                <p className="text-white/70 text-sm mb-5">Operations · Engineering · Customer experience</p>
                <p className="text-white/80 leading-relaxed text-sm mb-4">
                  Ronak runs operations, engineering, and the day-to-day customer experience. The home-buying mess
                  that sparked Bharat Estate was his — 47 site visits across Pune, Mumbai, and Bengaluru, 60% of the
                  &ldquo;owner&rdquo; numbers turning out to be brokers, 30% of listings simply not existing. He went
                  home, opened his laptop, and started writing what is now Bharat Estate.
                </p>
                <p className="text-white/80 leading-relaxed text-sm">
                  Today he owns every calculator on the site, every booking flow on the Home Services page, and every
                  founder email that lands at{' '}
                  <a className="text-[#D4AF37] hover:underline" href="mailto:contact@bharat.estate">contact@bharat.estate</a>.
                </p>
              </div>
            </div>
          </div>

          {/* Founder highlights row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            {founderHighlights.map((h, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-sm bg-[#F9F9F7] border border-border/40">
                <div className="h-9 w-9 rounded-sm bg-[#0F172A] text-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <h.icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{h.label}</p>
                  <p className="text-sm font-semibold text-[#0F172A] mt-0.5">{h.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Joint quote */}
          <blockquote className="mt-12 border-l-4 border-[#D4AF37] pl-6 py-2 italic text-[#0F172A] max-w-4xl" data-testid="founder-quote">
            <Quote className="h-6 w-6 text-[#D4AF37]/40 mb-2" />
            <p className="font-['Plus_Jakarta_Sans'] text-2xl leading-snug">
              &ldquo;If two brothers from a middle-class Indian family cannot navigate this market without 47 site
              visits and ₹85,000 wasted, our parents in Indore certainly cannot. That is the gap Bharat Estate exists
              to close — one honest listing, one transparent calculator, one verified pro at a time.&rdquo;
            </p>
            <footer className="text-sm text-muted-foreground mt-3 not-italic">— Bhavik Rana (CEO) &amp; Ronak Rana (COO), Co-Founders</footer>
          </blockquote>
        </div>
      </section>

      {/* THE JOURNEY / TIMELINE */}
      <section className="py-24 bg-[#0F172A] text-white">
        <div className="container-luxury">
          <div className="max-w-3xl mb-16">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-3">The Journey</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold leading-tight mb-4">
              From a frustrated boarding pass to a 24-city platform.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed">
              Bharat Estate is barely a year old. Here&apos;s how we got here — and where we are going next.
            </p>
          </div>

          <div className="relative" data-testid="about-timeline">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/15 -translate-x-1/2 md:-translate-x-px" aria-hidden="true" />
            <div className="space-y-12">
              {milestones.map((m, i) => (
                <div key={i} className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-start ${i % 2 === 0 ? '' : 'md:[direction:rtl]'}`}>
                  <div className={`pl-12 md:pl-0 ${i % 2 === 0 ? 'md:text-right md:pr-8' : 'md:[direction:ltr] md:pl-8'}`}>
                    <span className="inline-block px-3 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-xs font-bold uppercase tracking-wider mb-3 border border-[#D4AF37]/30">
                      {m.year}
                    </span>
                    <h3 className="font-['Plus_Jakarta_Sans'] text-2xl md:text-3xl font-bold mb-2">{m.title}</h3>
                    <p className="text-white/70 text-sm leading-relaxed">{m.body}</p>
                  </div>
                  <div className="hidden md:block" />
                  <div className="absolute left-4 md:left-1/2 top-2 -translate-x-1/2 h-4 w-4 rounded-full bg-[#D4AF37] ring-4 ring-[#0F172A]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY BHARAT — VASTU */}
      <section className="py-24 bg-white">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Why &ldquo;Bharat&rdquo; Estate</p>
              <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] leading-tight mb-6">
                Built for how Indians actually buy homes.
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  We did not call ourselves &ldquo;India Estate&rdquo; or &ldquo;Realty Hub&rdquo; or &ldquo;HomeX&rdquo;
                  on purpose. Bharat is not just a translation of India — it is a worldview. It includes Indore and
                  Itanagar, Coimbatore and Cuttack, the joint family in Lucknow and the NRI in San Jose buying for his
                  parents back home.
                </p>
                <p>
                  Indian home decisions are <strong className="text-[#0F172A]">family decisions</strong>. They involve
                  Vastu, muhurat, parental approval, joint ownership, ancestral considerations. Most platforms treat
                  these as friction. We treat them as features.
                </p>
                <p>
                  This is why Vastu compliance scoring is on our roadmap — and why we already let you filter by
                  &ldquo;direction-facing&rdquo; in the search experience. It is why our Stamp Duty Calculator handles
                  female-buyer rebates automatically across 14 states. It is why our Glossary explains
                  &ldquo;Brahmasthan&rdquo; as cleanly as it explains &ldquo;Carpet Area&rdquo;.
                </p>
              </div>
            </div>

            <div className="bg-[#0F172A] rounded-sm p-8 md:p-10 text-white relative overflow-hidden">
              <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-[#D4AF37]/10 blur-3xl" aria-hidden="true" />
              <Compass className="h-10 w-10 text-[#D4AF37] mb-4" />
              <h3 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold mb-4">The Vastu gap, in numbers</h3>
              <div className="space-y-5">
                <div className="flex items-baseline gap-3">
                  <span className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#D4AF37]">73%</span>
                  <p className="text-white/80 text-sm">of Indian home buyers say Vastu influences their final decision</p>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#D4AF37]">0</span>
                  <p className="text-white/80 text-sm">major Indian property portals offer Vastu compliance filters</p>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#D4AF37]">₹3–8K</span>
                  <p className="text-white/80 text-sm">average fee Indian families pay a separate Vastu consultant per shortlisted home</p>
                </div>
              </div>
              <p className="text-xs text-white/50 mt-6 leading-relaxed">
                Source: Internal research across 200+ buyers, plus published consumer surveys (Anarock 2024, Knight
                Frank India 2024). Bharat Estate is building the first integrated Vastu engine for Indian listings — coming Q2 2026.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE'VE BUILT */}
      <section className="py-24 bg-[#F9F9F7]">
        <div className="container-luxury">
          <SectionTitle
            eyebrow="What we&apos;ve built"
            title="A toolkit so complete, you can decide on a home in one sitting."
            kicker="Every tool below is free for buyers. Forever. We monetise via owner-listing memberships — never via you."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {tools.map((t) => (
              <Link
                key={t.href}
                to={t.href}
                className="group bg-white p-6 rounded-sm border border-border/50 hover:border-[#D4AF37] hover:shadow-md transition-all"
                data-testid={`about-tool-${t.href.replace(/\//g, '')}`}
              >
                <div className="h-11 w-11 rounded-sm bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center mb-4 group-hover:bg-[#D4AF37] group-hover:text-[#0F172A] transition-colors">
                  <t.icon className="h-5 w-5" />
                </div>
                <p className="font-semibold text-[#0F172A] text-sm mb-1">{t.name}</p>
                <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  Open <ArrowRight className="h-3 w-3" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES GRID */}
      <section className="py-24 bg-white">
        <div className="container-luxury">
          <SectionTitle
            eyebrow="What we believe"
            title="Six rules we will not compromise on."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {values.map((v, i) => (
              <div key={i} className="flex items-start gap-4 p-6 rounded-sm bg-[#F9F9F7] border border-border/40 hover:border-[#D4AF37]/50 transition-colors" data-testid={`value-card-${i}`}>
                <div className="h-11 w-11 rounded-sm bg-[#0F172A] text-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <v.icon className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F172A] text-base mb-1.5">{v.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{v.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="py-24 bg-[#F9F9F7]">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Coverage</p>
              <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] leading-tight mb-6">
                Live in 24+ cities across India.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We launched in Mumbai, Pune, and Bengaluru — and within the first 90 days expanded into Tier-2 markets
                that legacy portals barely cover. Indore, Bhubaneswar, Coimbatore, Lucknow, Nashik — all live, all with
                native city-level listing pages and stamp duty rules.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Don&apos;t see your city yet? Email us. We add cities in the order our users ask for them.
              </p>
              <Link to="/properties">
                <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-6 py-5" data-testid="about-browse-cities-btn">
                  Browse by City <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {cities.map((c) => (
                  <div key={c} className="flex items-center gap-2 px-4 py-3 bg-white rounded-sm border border-border/50 hover:border-[#D4AF37] hover:shadow-sm transition-all">
                    <MapPin className="h-3.5 w-3.5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-sm font-medium text-[#0F172A]">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST / CREDENTIALS */}
      <section className="py-24 bg-white">
        <div className="container-luxury">
          <SectionTitle
            eyebrow="Why trust us"
            title="The boring details that actually matter."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              { icon: ShieldCheck, h: 'Razorpay Live mode', b: 'PCI-DSS compliant payment processing. Your card never touches our servers.' },
              { icon: Receipt, h: 'GST-compliant invoicing', b: '18% GST is broken out separately. Auto-generated PDF invoices on every transaction.' },
              { icon: ScrollText, h: 'RERA-aware listings', b: 'Where applicable, we surface RERA registration numbers right on the listing card.' },
              { icon: Lightbulb, h: 'Founder-accessible support', b: 'No call-centre tier-1 hell. Email contact@bharat.estate and you reach a real human, fast.' },
            ].map((t, i) => (
              <div key={i} className="text-center p-6 rounded-sm bg-[#F9F9F7] border border-border/40" data-testid={`trust-${i}`}>
                <div className="h-12 w-12 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center mx-auto mb-4">
                  <t.icon className="h-6 w-6" />
                </div>
                <h4 className="font-semibold text-[#0F172A] mb-2">{t.h}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{t.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="py-24 bg-[#0F172A] text-white">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <Rocket className="h-10 w-10 text-[#D4AF37] mb-4" />
              <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-3">What&apos;s next</p>
              <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold leading-tight mb-4">
                A six-month roadmap, not a five-year fantasy.
              </h2>
              <p className="text-white/70 leading-relaxed">
                We ship every two weeks. Here is what is actually being built right now — not what some pitch deck
                claims will exist someday.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { h: 'Vastu Compliance Engine', b: 'Direction, entry, kitchen, brahmasthan scoring on every listing. Free for buyers.' },
                { h: 'AI Property Matchmaker', b: 'Trained on Indian buyer behaviour, not US Zillow data. Surfaces homes you would actually want.' },
                { h: 'WhatsApp-first agent flow', b: 'Owners get inquiries on WhatsApp natively. Zero email-CRM nonsense.' },
                { h: 'NRI Buyer Dashboard', b: 'FEMA, repatriation, joint-ownership, and currency-tracking — built for the diaspora.' },
                { h: 'Builder Microsites', b: 'Pre-launch projects with 3D walkthroughs, RERA docs, and live booking slots.' },
                { h: 'Auto-renewal Premium', b: 'Optional, transparent, one-tap to disable. Never silent.' },
              ].map((r, i) => (
                <div key={i} className="bg-white/5 border border-white/10 p-5 rounded-sm hover:bg-white/10 transition-colors">
                  <CheckCircle2 className="h-5 w-5 text-[#D4AF37] mb-3" />
                  <h4 className="font-semibold text-base mb-1.5">{r.h}</h4>
                  <p className="text-xs text-white/65 leading-relaxed">{r.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="py-24 bg-[#F9F9F7]">
        <div className="container-luxury max-w-5xl">
          <div className="bg-white rounded-sm border border-border/50 shadow-md p-10 md:p-14 text-center">
            <Building2 className="h-10 w-10 text-[#D4AF37] mx-auto mb-5" />
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-4 leading-tight">
              Want to talk to us?
            </h2>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Press, partnerships, complaints, compliments, or just &ldquo;hey nice product&rdquo;. Every email lands
              in a real inbox — usually answered within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 mb-8">
              <a href="mailto:contact@bharat.estate" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors text-sm font-semibold" data-testid="about-contact-email-btn">
                <Mail className="h-4 w-4" /> contact@bharat.estate
              </a>
              <a href="tel:+919465024516" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[#D4AF37] text-[#0F172A] hover:bg-[#B8932F] transition-colors text-sm font-bold" data-testid="about-contact-phone-btn">
                <Phone className="h-4 w-4" /> +91 94650 24516
                <span className="text-[10px] uppercase tracking-wider opacity-70 ml-1">India</span>
              </a>
              <a href="tel:+13652752286" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[#D4AF37] text-[#0F172A] hover:bg-[#B8932F] transition-colors text-sm font-bold" data-testid="about-contact-phone-intl-btn">
                <Phone className="h-4 w-4" /> +1 (365) 275-2286
                <span className="text-[10px] uppercase tracking-wider opacity-70 ml-1">US / Canada · NRI</span>
              </a>
            </div>
            <p className="text-xs text-muted-foreground text-center mb-6">
              Prefer WhatsApp? Both numbers above are WhatsApp-enabled.
            </p>
            <div className="pt-6 border-t border-border/40">
              <p className="text-xs text-muted-foreground">
                Serving 24+ Indian
                cities · For grievances, see our{' '}
                <Link to="/grievance" className="text-[#D4AF37] hover:underline">Grievance Redressal</Link> page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA STRIP */}
      <section className="py-16 bg-[#D4AF37]">
        <div className="container-luxury flex flex-col md:flex-row items-center justify-between gap-6 text-[#0F172A]">
          <div className="text-center md:text-left">
            <p className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold leading-tight">
              Stop scrolling. Start filtering.
            </p>
            <p className="text-sm mt-1 opacity-80">Find your next home with calculators that actually do the math for you.</p>
          </div>
          <div className="flex gap-3 flex-wrap justify-center">
            <Link to="/quiz">
              <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-6 py-5 font-semibold" data-testid="about-final-quiz-btn">
                Take the 60-sec Quiz <Sparkles className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
            <Link to="/properties?listing_type=buy">
              <Button variant="outline" className="border-[#0F172A] bg-transparent text-[#0F172A] hover:bg-[#0F172A] hover:text-white px-6 py-5 font-semibold" data-testid="about-final-browse-btn">
                Browse Properties
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;
